import styles from '@components/Card2/Card/Card.module.scss';
import EmailFields from '@components/Card2/CardFormFields/EmailFields/EmailFields';
import { CardItemEditor } from '@components/Card2/CardItemEditor';
import { canDeleteCardItem } from '@components/Card2/utils';
import { cardEditContext } from '@context/card-edit-context';
import { cardModeContext } from '@context/card-mode-context';
import { useCardFieldState } from '@hooks/use-card-field-state';
import useNode from '@hooks/use-node';
import { useContext, useState } from 'react';

import type { RowProps } from '../../types';
import { getHref, getNewCardFieldLexorank, useCardFieldMutations } from '../../utils';
import { FieldEditor } from '../FieldEditor';
import { FieldRow } from '../FieldRow';
import EmailContent from './Content';
import type { CardField, ValueProvider } from '.cache/__types__';

export function Email({ cardId, field, index, size, ...restProps }: RowProps): JSX.Element {
  const [fieldNode, fieldRef] = useNode<HTMLDivElement | HTMLButtonElement>();
  const [fieldBtnNode, fieldBtnRef] = useNode<HTMLDivElement | HTMLButtonElement>();
  const [tempValueProviderForTour, setTempValueProviderForTour] = useState<ValueProvider | null>(null);

  const cardFieldMutations = useCardFieldMutations(cardId, field);
  const { editPageMode, setPendingCardField, activeAttribute, flavour } = useContext(cardEditContext);
  const { mode } = useContext(cardModeContext);

  const fieldState = useCardFieldState(field, 'member');

  const onSave = (newValueProvider?: ValueProvider) => {
    const stage = fieldState.value ? 'done' : 'todo';

    const sharedMutationValues: Omit<CardField, 'id' | 'attribute_type_id'> & { card_id: string } = {
      card_id: cardId,
      label: fieldState.label,
      label_locked: fieldState.labelLocked,
      is_optional: fieldState.isOptional,
      value: fieldState.value,
      value_provider: fieldState.isModified // Prevent updating the value provider if the user hasn't made any changes
        ? (newValueProvider ?? fieldState.valueProvider)
        : fieldState.valueProvider,
      value_locked: fieldState.valueLocked,
      instruction_text: fieldState.instruction ?? null,
      stage,
    };

    if (editPageMode === 'create') {
      if (!activeAttribute) {
        return;
      }

      cardFieldMutations.create(
        {
          ...sharedMutationValues,
          lexorank: getNewCardFieldLexorank(cardId),
          attribute_type_id: activeAttribute.id,
        },
        activeAttribute,
      );
      return;
    }

    if (!field.lexorank) {
      return;
    }

    cardFieldMutations.update({
      ...sharedMutationValues,
      id: field.id,
      card_id: cardId,
      lexorank: field.lexorank,
    });
  };

  const canDelete = canDeleteCardItem({
    flavour,
    isOptional: Boolean(field.is_optional),
    isTemplated: Boolean(field.template_id),
    valueProvider: field.value_provider,
  });

  const handleDelete = () => {
    if (!canDelete) return;
    cardFieldMutations.remove({
      card_id: cardId,
      id: field.id,
    });
  };

  const handleCancel = () => {
    fieldState.onCancel();
    setPendingCardField(undefined);
  };

  if (mode === 'clickable') {
    return (
      <a href={getHref(field)} className={styles['card-item']} rel="noreferrer">
        <EmailContent email={field.value ?? ''} label={field.label ?? ''} size={size} />
      </a>
    );
  }

  if (mode === 'read-only') {
    return (
      <div className={styles['card-item']} rel="noreferrer" style={{ cursor: 'default' }}>
        <EmailContent email={field.value ?? ''} label={field.label ?? ''} size={size} />
      </div>
    );
  }

  return (
    <>
      <FieldRow
        id={field.id}
        fieldValue={field.value ?? ''}
        index={index}
        isLocked={fieldState.fullyLocked}
        isLockedTooltipMessage={fieldState.lockedTooltipMessage}
        isOptional={field.is_optional}
        fieldBtnRef={fieldBtnRef}
        fieldRef={fieldRef}
        {...restProps}
      >
        <div className={styles['card-item']} data-edit-mode>
          <EmailContent email={fieldState.value} label={fieldState.label} size={size} />
        </div>
      </FieldRow>

      <FieldEditor
        title={field.attribute_type?.display_name || ''}
        id={field.id}
        updateTempValueProviderForTour={setTempValueProviderForTour}
        fieldBtnNode={fieldBtnNode}
        fieldNode={fieldNode}
        hasUnsavedChanges={field.value !== fieldState.value}
        positionDeps={[]}
        onCancel={handleCancel}
        onDelete={handleDelete}
        onSave={onSave}
        validator={() => true}
        isFieldLinkedToProfile={fieldState.valueProvider === 'profile'}
        unlinkCardField={onSave}
        canDelete={canDelete}
      >
        <CardItemEditor
          {...fieldState}
          tempValueProviderForTour={tempValueProviderForTour}
          showLabelWhenIndividual
          fieldType="email"
        >
          <EmailFields {...fieldState} />
        </CardItemEditor>
      </FieldEditor>
    </>
  );
}
